import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  IconButton,
  LightModeSemanticColors,
  Spacing,
  Tooltip,
  useSecondaryMenu,
} from '@enechain/ecloud-designsystem'
import { FaArrowFromLineRegular } from '@enechain/ecloud-icons'

import {
  type SubMenuItem,
  SubMenuItems,
} from '~/components/layouts/MainLayout/SubMenuItems'
import { TICKER_BAR_HEIGHT } from '~/components/layouts/MainLayout/TickerBar'
import { markSecondaryMenuClosedByUser } from '~/components/layouts/MainLayout/secondaryMenuAutoOpen'

type Props = {
  items: SubMenuItem[]
}

// DS の副 panel header (height = theme.space[12] = 48px) の中央に
// 32px の close button を absolute 配置する
const SECONDARY_HEADER_HEIGHT = 48
const CLOSE_BUTTON_SIZE = 32

// 副 panel の項目リストに内部スクロールを与えるための上限高さ。
//
// DS `SecondaryMenuLayout` の構造 (0.14.2 の sourcemap で実測):
//
//   VStack  position=absolute / height=full          ← panel (= viewport 高)
//     Flex  position=relative / column / flex=1
//       Flex  height={12}                            ← header 48px
//       Flex  column / gap=1 / paddingX=2 / paddingY=4   ← ここに本 component が入る
//
// 項目が入る Flex は `flex=1` も `minHeight=0` も `overflow` も持たないため、
// 中身が縦に伸びると **panel の下端を突き抜けてそのまま画面外に出る**。
// さらに DS の root は `position: fixed; inset: 0` なので document 自体が
// スクロールせず、溢れた項目に到達する手段が無くなる
// (システム管理 = 23 項目。次に多い需給の 9 項目を大きく超えるためここだけ顕在化した)。
//
// DS 側の Flex には app から props を渡せないので、本 component 側で高さの上限を
// 持ってスクロールさせる。design プロトタイプの
// `.sidebar-secondary-menu { flex: 1; overflow-y: auto }` と同じ扱いになる。
//
// viewport 全高から引くのは 3 つ:
//   1. テロップバー — `MainLayout` が DS root を `top: 48px` /
//      `height: calc(100vh - 48px)` に縮めているので、panel の実高は
//      viewport より `TICKER_BAR_HEIGHT` だけ低い。ここを引き忘れると
//      **スクロール領域の下端が画面外に出て最後の項目に届かない**。
//   2. panel header (`Flex height={12}` = 48px)
//   3. 項目リストの上下 padding (`paddingY={4}` = 16px × 2)
const SECONDARY_MENU_PADDING_Y = 16 // DS の paddingY={4} (上下それぞれ)
const SECONDARY_MENU_MAX_HEIGHT = `calc(100dvh - ${TICKER_BAR_HEIGHT} - ${String(
  SECONDARY_HEADER_HEIGHT + SECONDARY_MENU_PADDING_Y * 2,
)}px)`

const CloseButton = (): React.ReactElement => {
  const { t } = useTranslation()
  const { clearSecondaryMenu } = useSecondaryMenu()

  // × は「閉じておきたい」 という明示的な意思表示なので、 遷移のたびに開き直さない
  // よう記憶する (抑止するのは auto-open だけ。 nav の group をクリックすれば開く)。
  const handleClose = (): void => {
    markSecondaryMenuClosedByUser()
    clearSecondaryMenu()
  }

  return (
    <Tooltip content={t('common:action.close')}>
      <IconButton
        aria-label={t('common:action.close')}
        onClick={handleClose}
        size="sm"
        style={{
          position: 'absolute',
          top: (SECONDARY_HEADER_HEIGHT - CLOSE_BUTTON_SIZE) / 2,
          right: Spacing[2].value,
          width: `${CLOSE_BUTTON_SIZE}px`,
          height: `${CLOSE_BUTTON_SIZE}px`,
          color: LightModeSemanticColors.Text.midEmphasis.value,
          zIndex: 2,
        }}
        variant="ghost"
      >
        {/* design (include-nav.js の .sidebar-secondary-close) と同じ FaArrowFromLine。
          sidebar 側の折りたたみボタン (DS `OpenFirstlyMenuButton`) と同じ絵で
          同じ「畳む」意味を表す。 旧実装の `«` は Unicode 直書きでフォント依存に
          字形・光学サイズが揺れるため撤去 (design 2026-07-29 の差し替えに追随)。
          寸法 props は渡さない (icon-only ボタンは DS 既定)。 */}
        <FaArrowFromLineRegular />
      </IconButton>
    </Tooltip>
  )
}

// DS が副 panel 内に自動描画する collapse toggle (aria-label="Open Menu"
// の IconButton、 右下に absolute positioning) を hide する hook。
// 同 aria-label のボタンが sidebar 側にも存在するため、 副 panel の
// position: relative 祖先 (= inner wrapper) に限定して querySelector する。
const useHideSecondaryCollapseToggle = (
  anchorRef: React.RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (anchorRef.current == null) return
    let panel: HTMLElement | null = anchorRef.current.parentElement
    while (panel != null) {
      if (window.getComputedStyle(panel).position === 'relative') break
      panel = panel.parentElement
    }
    if (panel == null) return
    const button = panel.querySelector<HTMLElement>(
      'button[aria-label="Open Menu"]',
    )
    if (button == null) return
    const wrapper = button.parentElement
    if (wrapper != null) {
      wrapper.style.display = 'none'
    } else {
      button.style.display = 'none'
    }
  }, [anchorRef])
}

export const SecondaryMenuContent = ({ items }: Props): React.ReactElement => {
  const anchorRef = useRef<HTMLSpanElement>(null)
  useHideSecondaryCollapseToggle(anchorRef)

  return (
    <>
      <span ref={anchorRef} aria-hidden="true" style={{ display: 'none' }} />
      <CloseButton />
      {/* `display: flex` + `gap` を自分で持つ。 DS 側の入れ物は
          `Flex column gap="1"` で、 これまでは `SubMenuItems` が fragment を返して
          各行が直接その flex 子だったため 4px の間隔が付いていた。 素の `Box`
          (= block) で包むと flex 子が本 Box 1 つになり、 **行間が 0 になる**。 */}
      <Box
        flexDirection="column"
        gap={1}
        display="flex"
        overflowY="auto"
        maxHeight={SECONDARY_MENU_MAX_HEIGHT}
        data-testid="secondary-menu-scroll"
      >
        <SubMenuItems items={items} />
      </Box>
    </>
  )
}
